<script>
import { parse } from 'date-fns';
import { calculateTotalHours } from '../helpers/businessHour';
export default {
  components: {},
  props: {
    dayName: {
      type: String,
      required: true,
    },
    timeSlot: {
      type: Object,
      default: () => ({
        intervals: [],
        valid: true,
        openAllDay: false,
      }),
    },
  },
  emits: ['update'],
  computed: {
    isDayEnabled: {
      get() {
        return this.timeSlot.openAllDay || (this.timeSlot.intervals || []).length > 0;
      },
      set(value) {
        const newSlot = value
          ? { ...this.timeSlot, intervals: [{ from: '09:00', to: '17:00' }], valid: true, openAllDay: false }
          : { ...this.timeSlot, intervals: [], valid: true, openAllDay: false };
        this.$emit('update', this.withValidation(newSlot));
      },
    },
    isOpenAllDay: {
      get() {
        return this.timeSlot.openAllDay;
      },
      set(value) {
        const newSlot = value
          ? { ...this.timeSlot, intervals: [{ from: '00:00', to: '23:59' }], openAllDay: true }
          : { ...this.timeSlot, intervals: [{ from: '09:00', to: '17:00' }], openAllDay: false };
        this.$emit('update', this.withValidation(newSlot));
      },
    },
    hasError() {
      return !this.timeSlot.valid;
    },
    totalHours() {
      if (this.timeSlot.openAllDay) return '24h';
      return calculateTotalHours(this.timeSlot.intervals || []);
    },
  },
  methods: {
    withValidation(slot) {
      const intervals = slot.intervals || [];
      if (slot.openAllDay) return { ...slot, valid: true };
      if (!intervals.length) return { ...slot, valid: true };

      const normalized = intervals.map(interval => {
        const fromDate = parse(interval.from, 'HH:mm', new Date());
        const toDate = parse(interval.to, 'HH:mm', new Date());
        return {
          fromMinutes: fromDate.getHours() * 60 + fromDate.getMinutes(),
          toMinutes: toDate.getHours() * 60 + toDate.getMinutes(),
        };
      });

      if (normalized.some(i => i.toMinutes <= i.fromMinutes)) return { ...slot, valid: false };

      const sorted = [...normalized].sort((a, b) => a.fromMinutes - b.fromMinutes);
      for (let i = 1; i < sorted.length; i += 1) {
        if (sorted[i].fromMinutes < sorted[i - 1].toMinutes) return { ...slot, valid: false };
      }

      return { ...slot, valid: true };
    },
    updateInterval(index, patch) {
      const intervals = [...(this.timeSlot.intervals || [])];
      intervals[index] = { ...intervals[index], ...patch };
      this.$emit('update', this.withValidation({ ...this.timeSlot, intervals }));
    },
    addInterval() {
      const intervals = [...(this.timeSlot.intervals || [])];
      intervals.push({ from: '18:00', to: '21:00' });
      this.$emit('update', this.withValidation({ ...this.timeSlot, intervals, openAllDay: false }));
    },
    removeInterval(index) {
      const intervals = [...(this.timeSlot.intervals || [])];
      intervals.splice(index, 1);
      this.$emit('update', this.withValidation({ ...this.timeSlot, intervals }));
    },
  },
};
</script>

<template>
  <tr>
    <td class="ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4">
      <div class="flex items-center gap-2 min-h-16">
        <input
          v-model="isDayEnabled"
          name="enable-day"
          class="m-0"
          type="checkbox"
          :title="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.ENABLE')"
        />
        <span class="text-body-main text-n-slate-12 font-medium">
          {{ dayName }}
        </span>
      </div>
    </td>
    <td class="py-3 ltr:pr-3 rtl:pl-3">
      <div v-if="isDayEnabled" class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <input
            v-model="isOpenAllDay"
            name="enable-open-all-day"
            class="m-0"
            type="checkbox"
            :title="$t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')"
          />
          <span class="text-body-main text-n-slate-12">
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY') }}
          </span>
        </div>
        <template v-if="!isOpenAllDay">
          <div
            v-for="(interval, idx) in timeSlot.intervals"
            :key="`${dayName}-${idx}`"
            class="flex items-center gap-2"
          >
            <input
              type="time"
              :value="interval.from"
              class="rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-surface-1 py-2 px-3 text-sm text-n-slate-12 border-0"
              @change="e => updateInterval(idx, { from: e.target.value })"
            />
            <span class="text-n-slate-11 shrink-0">—</span>
            <input
              type="time"
              :value="interval.to"
              class="rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-surface-1 py-2 px-3 text-sm text-n-slate-12 border-0"
              @change="e => updateInterval(idx, { to: e.target.value })"
            />
            <button
              v-if="timeSlot.intervals.length > 1"
              type="button"
              class="text-n-ruby-9 text-xs font-semibold shrink-0"
              @click="removeInterval(idx)"
            >
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.REMOVE_INTERVAL') }}
            </button>
          </div>
          <button
            type="button"
            class="self-start text-xs font-semibold text-n-brand"
            @click="addInterval"
          >
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.ADD_INTERVAL') }}
          </button>
        </template>
        <span v-if="hasError" class="error text-label-small text-n-ruby-9">
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.VALIDATION_ERROR') }}
        </span>
      </div>
      <span v-else class="text-body-main text-n-slate-11">
        {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.UNAVAILABLE') }}
      </span>
    </td>
    <td class="py-3 ltr:pr-3 rtl:pl-3">
      <span
        v-if="isDayEnabled && !hasError"
        class="label bg-n-blue-3 text-n-blue-11 text-label-small inline-block px-2 py-1 rounded-lg cursor-default whitespace-nowrap"
      >
        {{ totalHours }}
      </span>
    </td>
  </tr>
</template>
